import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Chip, List, ListItem, ListItemText, Divider } from "@mui/material";

// Un évènement reçu il y a moins de ce délai est considéré comme un signe
// de connexion active — au-delà, on affiche "pas de trame récente" plutôt
// que d'affirmer une déconnexion qu'on ne peut pas vérifier directement
// (le serveur ne garde pas d'état de connexion, juste l'historique des
// trames reçues).
const CONNECTION_TIMEOUT_MS = 20000;
const POLL_INTERVAL_MS = 3000;
const MAX_ROWS = 10;

// Statut du relais LOCAL (127.0.0.1) tel que transmis par le script
// Python — distinct de la réception par le Timeserver, puisque ce chemin
// n'a ni file d'attente ni retry (contrairement à l'envoi distant).
const LOCAL_STATUS_INFO = {
    delivered: { label: "🟢 Delivered to local chrono", color: "success" },
    no_client: { label: "🔴 No local chrono connected", color: "error" },
    write_failed: { label: "🔴 Failed to send to local chrono", color: "error" },
    null: { label: "⚪ Local relay disabled", color: "default" },
};

function localStatusInfo(status) {
    return LOCAL_STATUS_INFO[status ?? "null"] || { label: status, color: "default" };
}

export default function LiveChronoFeed({ channel = "4" }) {
    const [events, setEvents] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const load = () => {
            fetch(`/api/events/${encodeURIComponent(channel)}`)
                .then((r) => {
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    return r.json();
                })
                .then((data) => {
                    if (!cancelled) {
                        setEvents((data || []).slice(0, MAX_ROWS));
                        setError("");
                    }
                })
                .catch((err) => {
                    if (!cancelled) setError(err.message);
                });
        };

        load();
        const interval = setInterval(load, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [channel]);

    const lastEvent = events[0];
    const isConnected =
        !!lastEvent &&
        !!lastEvent.received_at &&
        Date.now() - new Date(lastEvent.received_at).getTime() < CONNECTION_TIMEOUT_MS;

    // Statut du relais local basé sur la toute dernière trame reçue — ne
    // reflète que l'instant de cette trame précise, pas un état permanent
    // (le script ne "pousse" pas d'état, seulement à chaque trame envoyée).
    const lastLocalStatus = lastEvent ? localStatusInfo(lastEvent.local_status) : null;

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, "@media print": { display: "none" } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    Chrono frames (channel {channel})
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Chip
                        label={isConnected ? "🟢 Timeserver up to date" : "🔴 No recent frame"}
                        color={isConnected ? "success" : "default"}
                        size="small"
                    />
                    {lastLocalStatus && (
                        <Chip label={lastLocalStatus.label} color={lastLocalStatus.color} size="small" />
                    )}
                </Box>
            </Box>

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {error}
                </Typography>
            )}

            {events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No frames received yet on this channel.
                </Typography>
            ) : (
                <List dense sx={{ maxHeight: 220, overflowY: "auto", py: 0 }}>
                    {events.map((ev, i) => {
                        const localInfo = localStatusInfo(ev.local_status);
                        return (
                            <React.Fragment key={ev.id}>
                                <ListItem
                                    sx={{ py: 0.5 }}
                                    secondaryAction={
                                        <Chip label={localInfo.label} color={localInfo.color} size="small" />
                                    }
                                >
                                    <ListItemText
                                        primary={`Bib ${ev.candidate ?? "—"}  —  seq ${ev.sequence ?? "—"}`}
                                        secondary={
                                            `${ev.chrono_id || "?"} — ` +
                                            (ev.received_at
                                                ? new Date(ev.received_at).toLocaleTimeString("fr-CH")
                                                : "—")
                                        }
                                        sx={{ pr: 20 }}
                                    />
                                </ListItem>
                                {i < events.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        );
                    })}
                </List>
            )}
        </Paper>
    );
}